import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { AuthCardIntro, AuthCardTopBar } from "../components/AuthCardHeader";
import { PasswordStrengthIndicator } from "../components/PasswordStrengthIndicator";
import {
  getPasswordRequirements,
  isPasswordValid,
} from "../utils/passwordValidation";
import {
  useCompleteUserInvite,
  useRequestUserInvite,
  useVerifyUserInvite,
} from "../mutations";
import { ROUTES } from "../../../shared/utils";

export const VerifyInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const verifyInviteMutation = useVerifyUserInvite();
  const completeInviteMutation = useCompleteUserInvite();
  const requestInviteMutation = useRequestUserInvite();

  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifiedUserId, setVerifiedUserId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [verificationError, setVerificationError] = useState<string>("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [completeError, setCompleteError] = useState<string>("");
  const [completeSuccess, setCompleteSuccess] = useState<string>("");

  const [resendEmail, setResendEmail] = useState("");
  const [resendTouched, setResendTouched] = useState(false);
  const [resendError, setResendError] = useState<string>("");
  const [resendSuccess, setResendSuccess] = useState<string>("");

  const passwordRequirements = useMemo(() => {
    return getPasswordRequirements(password, confirmPassword);
  }, [password, confirmPassword]);

  const isPasswordValidCheck = isPasswordValid(passwordRequirements);
  const showPasswordError = touched && !isPasswordValidCheck;
  const showConfirmPasswordError =
    touched &&
    !passwordRequirements.find((req) => req.key === "passwordsMatch")?.isValid;

  const isResendEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail);
  const showResendEmailError = resendTouched && !isResendEmailValid;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setVerificationError("");
      setVerifiedEmail(null);
      setVerifiedUserId(null);
      setExpiresAt(null);

      if (!token) {
        setVerificationError("Invite link is missing a token.");
        return;
      }

      try {
        const result = await verifyInviteMutation.mutateAsync({ token });
        if (cancelled) return;

        if (!result?.valid) {
          setVerificationError(result?.error || "Invite token invalid or expired.");
          return;
        }

        if (!result.email || !result.userId) {
          setVerificationError("Invite token invalid or expired.");
          return;
        }

        setVerifiedEmail(result.email);
        setVerifiedUserId(result.userId);
        setExpiresAt(result.expiresAt ?? null);
      } catch (error: unknown) {
        if (cancelled) return;
        setVerificationError("Invite token invalid or expired.");
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCompleteInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    setCompleteError("");
    setCompleteSuccess("");

    if (!token || !isPasswordValidCheck) return;

    try {
      const result = await completeInviteMutation.mutateAsync({
        token,
        newPassword: password,
      });

      setCompleteSuccess(result?.message || "Password set successfully.");
      setTimeout(() => navigate(ROUTES.LOGIN), 800);
    } catch (error: unknown) {
      let errorMessage = "Invite token invalid or expired.";

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string; message?: string } };
        };
        errorMessage =
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setCompleteError(errorMessage);
    }
  };

  const handleResendInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setResendTouched(true);
    setResendError("");
    setResendSuccess("");

    if (!isResendEmailValid) return;

    try {
      const result = await requestInviteMutation.mutateAsync({
        email: resendEmail.trim(),
      });
      setResendSuccess(result?.message || "Invite sent. Please check your email.");
    } catch (error: unknown) {
      let errorMessage = "Unable to request a new invite. Please contact support.";

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string; message?: string } };
        };
        errorMessage =
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setResendError(errorMessage);
    }
  };

  const isVerifying = verifyInviteMutation.isPending;
  const isVerified = !!verifiedEmail && !!verifiedUserId && !verificationError;

  if (isVerifying) {
    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <AuthCardTopBar />
            <CircularProgress />
            <Typography color="text.secondary">Verifying your invite…</Typography>
          </Stack>
        </Box>
      </Card>
    );
  }

  if (!isVerified) {
    return (
      <Card
        component="form"
        onSubmit={handleResendInvite}
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box sx={{ p: 4 }}>
          <Stack spacing={4}>
            <AuthCardTopBar />
            <AuthCardIntro
              title="Invite link expired"
              subtitle="This invite link is invalid or has expired. You can request a new invite below."
            />

            {verificationError && <Alert severity="error">{verificationError}</Alert>}
            {resendError && <Alert severity="error">{resendError}</Alert>}
            {resendSuccess && <Alert severity="success">{resendSuccess}</Alert>}

            <TextField
              fullWidth
              label="Email address"
              value={resendEmail}
              type="email"
              onChange={(event) => setResendEmail(event.target.value)}
              onBlur={() => setResendTouched(true)}
              error={showResendEmailError}
              placeholder="Enter your email address"
              helperText={showResendEmailError ? "Please enter a valid email address." : ""}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={!resendEmail.length || requestInviteMutation.isPending}
              sx={{ mt: 2 }}
            >
              {requestInviteMutation.isPending ? "Sending…" : "Send new invite"}
            </Button>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", fontWeight: 400 }}
            >
              Already have access?{" "}
              <Link component={RouterLink} to={ROUTES.LOGIN} underline="hover" sx={{ fontWeight: 600 }}>
                Back to login
              </Link>
            </Typography>
          </Stack>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      component="form"
      onSubmit={handleCompleteInvite}
      variant="outlined"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "divider",
        borderRadius: 4,
      }}
    >
      <Box sx={{ p: 4 }}>
        <Stack spacing={4}>
          <AuthCardTopBar />
          <AuthCardIntro
            title="Welcome to Peak4"
            subtitle="Set a password to activate your account."
          />

          {completeError && <Alert severity="error">{completeError}</Alert>}
          {completeSuccess && <Alert severity="success">{completeSuccess}</Alert>}

          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email address"
              value={verifiedEmail}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New password"
              value={password}
              type={showPassword ? "text" : "password"}
              onChange={(event) => setPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showPasswordError}
              placeholder="Enter your new password"
              helperText={showPasswordError ? "Password must meet all requirements below." : ""}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm password"
              value={confirmPassword}
              type={showConfirmPassword ? "text" : "password"}
              onChange={(event) => setConfirmPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showConfirmPasswordError}
              placeholder="Confirm your new password"
              helperText={showConfirmPasswordError ? "Passwords do not match." : ""}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {password && (
            <PasswordStrengthIndicator
              requirements={passwordRequirements}
              showProgress={true}
            />
          )}

          {expiresAt && (
            <Typography variant="caption" color="text.secondary">
              Invite expires: {new Date(expiresAt).toLocaleString()}
            </Typography>
          )}

          <Button
            type="submit"
            size="large"
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            disabled={
              !password.length ||
              !confirmPassword.length ||
              !isPasswordValidCheck ||
              completeInviteMutation.isPending
            }
            sx={{ mt: 2 }}
          >
            {completeInviteMutation.isPending ? "Setting password…" : "Set password"}
          </Button>
        </Stack>
      </Box>
    </Card>
  );
};

